import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar } from 'recharts'
import { FARMS, FARM_COMPONENTS, TEMPLATE_COMPONENTS, COLORS, band, bandLabel } from '../utils/RiversafeUtils.js';
import '../assets/styles/RiverSafeDashboard.css';

function Droplet({ score, size=120 }){
  const color = COLORS[band(score)]
  const id = `grad-${band(score)}-${size}-${score}`
  return (
    <svg width={size} height={size} viewBox="0 0 64 64">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} /><stop offset="100%" stopColor={color} />
        </linearGradient>
      </defs>
      <path d="M32 4 C24 16 14 26 14 38 C14 50 22 58 32 58 C42 58 50 50 50 38 C50 26 40 16 32 4 Z" fill={`url(#${id})`} stroke={color} strokeWidth="1.75"/>
      <text x="32" y="40" textAnchor="middle" fontSize="18" fontWeight="700" fill="#fff">{score}</text>
    </svg>
  )
}

const WeightBadge = ({w}) => <span style={{fontSize:'0.75rem', padding:'0.25rem 0.5rem', borderRadius:'0.5rem', backgroundColor:'#eee', color:'#333', border:'1px solid #ccc'}}>{w}% weight</span>

export default function FarmDetail(){
  const { code } = useParams()
  const farm = FARMS.find(f=>f.code===code) || { code, score: 0 }
  const components = FARM_COMPONENTS[code] || TEMPLATE_COMPONENTS
  const overallColor = COLORS[band(farm.score)]
  const radarData = components.map(c=>({metric:c.key, score:c.score}))

  return (
    <div className="farm-detail-container">
      <div className="kicker">A single, clear water quality metric — <span style={{color:'#007acc'}}>Clean River Beef</span>.</div>

      <div className="header-flex">
        <div>
          <h1>RiverSafe — Farm Detail</h1>
          <p style={{color:'#555'}}>{code}</p>
        </div>
        <div className="card" style={{textAlign:'right'}}>
          <div style={{fontSize:'0.75rem', color:'#555'}}>RiverSafe Score</div>
          <div style={{fontSize:'2rem', fontWeight:'700', color:overallColor}}>{farm.score}</div>
          <div style={{fontSize:'0.75rem', color:overallColor}}>{bandLabel(farm.score)}</div>
        </div>
      </div>

      <div className="component-grid" style={{gridTemplateColumns:'160px 1fr'}}>
        <div className="card droplet-container"><Droplet score={farm.score} /></div>
        <div className="card">
          <strong>How the score is calculated:</strong> The RiverSafe Score is a weighted blend of four components.
          <div style={{marginTop:'0.5rem', fontSize:'0.875rem', color:'#555'}}>50 = balanced · &gt;85 = excellent · &lt;50 = at risk</div>
        </div>
      </div>

      <div className="component-grid">
        {components.map(c=>{
          const cColor = COLORS[band(c.score)]
          return (
            <div className="component-card" key={c.key}>
              <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                <div>{c.key}</div>
                <WeightBadge w={c.weight}/>
              </div>
              <div style={{fontSize:'0.875rem', color:'#555', marginTop:'0.25rem'}}>{c.explain}</div>
              <div style={{display:'flex', gap:'0.5rem', marginTop:'0.5rem'}}>
                <div style={{flex:1, height:'1rem', backgroundColor:'#eee', borderRadius:'0.25rem'}}>
                  <div style={{width:`${c.score}%`, height:'100%', backgroundColor:cColor, borderRadius:'0.25rem'}}></div>
                </div>
                <div style={{fontWeight:'700', color:cColor}}>{c.score}</div>
              </div>
              <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'0.5rem', marginTop:'0.5rem', fontSize:'0.875rem', color:'#555'}}>
                <div>
                  <div style={{fontWeight:'500', marginBottom:'0.25rem'}}>Why this score</div>
                  <ul style={{paddingLeft:'1rem'}}>{c.reasons.map((r,i)=><li key={i}>{r}</li>)}</ul>
                </div>
                <div>
                  <div style={{fontWeight:'500', marginBottom:'0.25rem'}}>Recommended next actions</div>
                  <ul style={{paddingLeft:'1rem'}}>{c.recs.map((r,i)=><li key={i}>{r}</li>)}</ul>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="card">
        <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem'}}>
          <h3>Component Scores</h3>
          <span style={{fontSize:'0.875rem', color:'#555'}}>Higher is better</span>
        </div>
        <div style={{width:'100%', height:300}}>
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={radarData}>
              <PolarGrid stroke="#ccc" />
              <PolarAngleAxis dataKey="metric" tick={{fill:'#555', fontSize:12}} />
              <Radar name="Score" dataKey="score" stroke={overallColor} fill={overallColor} fillOpacity={0.25}/>
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{marginTop:'1rem', fontSize:'0.875rem'}}><Link to="/riversafeportfolio" style={{color:'#007acc'}}>← Back to portfolio</Link></div>

      <footer>© {new Date().getFullYear()} x10AI • RiverSafe™ — “Clean River Beef”</footer>
    </div>
  )
}
